import React, { useEffect, useState } from 'react';
import TextInput from '../../widgets/TextInput';
import NumberInput from '../../widgets/NumberInput';
import SelectInput from '../../widgets/SelectInput';
import CheckboxInput from '../../widgets/CheckboxInput';
import { ProductOrService } from '../../../models/ProductOrService';
import { VatRate } from '../../../models/VatRate';
import { Supplier } from '../../../models/Supplier';
import { FaSave, FaTimes, FaTrash } from 'react-icons/fa';
import ModalDialog from '../../common/ModalDialog/ModalDialog';
import './ProductOrServiceDialog.scss';
import { useTranslation } from 'react-i18next';

interface ProductOrServiceDialogProps {
  open: boolean;
  product: ProductOrService | null;
  vatRates: VatRate[];
  suppliers: Supplier[];
  onSave: (product: ProductOrService) => void;
  onDelete: (product: ProductOrService) => void;
  onClose: () => void;
}

const emptyProduct: ProductOrService = {
  id: '',
  articleCode: '',
  name: '',
  category: '',
  description: '',
  isActive: true,
  unit: '',
  unitPriceExVat: 0,
  purchasePriceExVat: 0,
  vat: undefined,
  revenueAccountCode: '',
  costAccountCode: '',
  preferredSupplierId: undefined,
  supplierArticleCode: '',
  notes: '',
};

const ProductOrServiceDialog: React.FC<ProductOrServiceDialogProps> = ({
  open, product, vatRates, suppliers, onSave, onDelete, onClose,
}) => {
  const { t } = useTranslation(['common', 'screens']);
  const [data, setData] = useState<ProductOrService>(product ?? emptyProduct);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const unitOptions = React.useMemo(() => [
    { label: t('screens:productOrServiceDialog.units.piece'), value: 'st' },
    { label: t('screens:productOrServiceDialog.units.hour'), value: 'uur' },
  ], [t]);

  useEffect(() => {
    setData(product ?? { ...emptyProduct });
    setShowDeleteConfirm(false);
  }, [product, open]);

  if (!open) return null;

  const isNew = !product?.id;
  const set = <K extends keyof ProductOrService>(field: K, value: ProductOrService[K]) =>
    setData(prev => ({ ...prev, [field]: value }));

  const handleVatChange = (vatId: string) => {
    set('vat', vatRates.find(v => v.id === vatId) ?? undefined);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(data);
  };

  const vatOptions = vatRates.map(v => ({
    label: `${v.name} (${v.percentage}%)`,
    value: v.id,
  }));

  const supplierOptions = [
    { label: t('screens:productOrServiceDialog.none'), value: '' },
    ...suppliers.map(s => ({ label: s.name, value: s.id })),
  ];

  return (
    <ModalDialog
      open={open}
      title={isNew ? t('screens:productOrServiceDialog.newTitle') : t('screens:productOrServiceDialog.editTitle')}
      subtitle={!isNew ? product?.name : undefined}
      onClose={onClose}
      onSubmit={handleSave}
      width={900}
      footerLeft={!isNew ? (
        showDeleteConfirm ? (
          <div className="pos-dialog__delete-confirm">
            <span className="pos-dialog__delete-confirm-text">{t('common:areYouSure')}</span>
            <button type="button" className="btn btn-danger btn-sm" onClick={() => onDelete(data)}>
              <FaTrash style={{ marginRight: 5 }} />{t('common:yesDelete')}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowDeleteConfirm(false)}>
              {t('common:cancel')}
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => setShowDeleteConfirm(true)}>
            <FaTrash style={{ marginRight: 5 }} />{t('common:delete')}
          </button>
        )
      ) : undefined}
      footerRight={
        <>
          <button type="submit" className="btn btn-primary">
            <FaSave style={{ marginRight: 5 }} />{t('common:save')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            <FaTimes style={{ marginRight: 5 }} />{t('common:close')}
          </button>
        </>
      }
    >
      {/* Section: General */}
      <div className="dialog-section">
        <div className="dialog-section-title">{t('screens:productOrServiceDialog.sections.general')}</div>
        <div className="dialog-row">
          <div className="dialog-col">
            <TextInput
              label={t('screens:productOrServiceDialog.fields.articleCode')}
              value={data.articleCode ?? ''}
              onChange={v => set('articleCode', v)}
            />
          </div>
          <div className="dialog-col dialog-col--wide">
            <TextInput
              label={t('screens:productOrServiceDialog.fields.name')}
              value={data.name ?? ''}
              required
              onChange={v => set('name', v)}
            />
          </div>
          <div className="pos-dialog__col--checkbox">
            <CheckboxInput
              label={t('screens:productOrServiceDialog.fields.isActive')}
              checked={data.isActive}
              onChange={v => set('isActive', v)}
            />
          </div>
        </div>
        <div className="dialog-row">
          <div className="dialog-col">
            <TextInput
              label={t('screens:productOrServiceDialog.fields.category')}
              value={data.category ?? ''}
              onChange={v => set('category', v)}
            />
          </div>
          <div className="dialog-col dialog-col--wide">
            <TextInput
              label={t('screens:productOrServiceDialog.fields.description')}
              value={data.description ?? ''}
              onChange={v => set('description', v)}
            />
          </div>
        </div>
      </div>

      {/* Section: Pricing */}
      <div className="dialog-section">
        <div className="dialog-section-title">{t('screens:productOrServiceDialog.sections.pricing')}</div>
        <div className="dialog-row">
          <div className="dialog-col">
            <SelectInput
              label={t('screens:productOrServiceDialog.fields.unit')}
              value={data.unit ?? ''}
              options={unitOptions}
              onChange={v => set('unit', v)}
            />
          </div>
          <div className="dialog-col">
            <NumberInput
              label={t('screens:productOrServiceDialog.fields.salesPrice')}
              value={data.unitPriceExVat}
              min={0}
              onChange={v => set('unitPriceExVat', v)}
            />
          </div>
          <div className="dialog-col">
            <NumberInput
              label={t('screens:productOrServiceDialog.fields.costs')}
              value={data.purchasePriceExVat}
              min={0}
              onChange={v => set('purchasePriceExVat', v)}
            />
          </div>
          <div className="dialog-col">
            <SelectInput
              label={t('screens:productOrServiceDialog.fields.vatRate')}
              value={data.vat?.id ?? ''}
              options={vatOptions}
              onChange={handleVatChange}
            />
          </div>
        </div>
      </div>

      {/* Section: Accounting */}
      <div className="dialog-section">
        <div className="dialog-section-title">{t('screens:productOrServiceDialog.sections.accounting')}</div>
        <div className="dialog-row">
          <div className="dialog-col">
            <TextInput
              label={t('screens:productOrServiceDialog.fields.revenueAccount')}
              value={data.revenueAccountCode ?? ''}
              onChange={v => set('revenueAccountCode', v)}
            />
          </div>
          <div className="dialog-col">
            <TextInput
              label={t('screens:productOrServiceDialog.fields.costAccount')}
              value={data.costAccountCode ?? ''}
              onChange={v => set('costAccountCode', v)}
            />
          </div>
        </div>
      </div>

      {/* Section: Supplier */}
      <div className="dialog-section">
        <div className="dialog-section-title">{t('screens:productOrServiceDialog.sections.supplier')}</div>
        <div className="dialog-row">
          <div className="dialog-col dialog-col--wide">
            <SelectInput
              label={t('screens:productOrServiceDialog.fields.preferredSupplier')}
              value={data.preferredSupplierId ?? ''}
              options={supplierOptions}
              onChange={v => set('preferredSupplierId', v || undefined)}
            />
          </div>
          <div className="dialog-col">
            <TextInput
              label={t('screens:productOrServiceDialog.fields.supplierArticleCode')}
              value={data.supplierArticleCode ?? ''}
              onChange={v => set('supplierArticleCode', v)}
            />
          </div>
        </div>
      </div>

      {/* Section: Notes */}
      <div className="dialog-section">
        <div className="dialog-section-title">{t('screens:productOrServiceDialog.sections.notes')}</div>
        <div className="dialog-row">
          <div className="dialog-col dialog-col--full">
            <TextInput
              label={t('screens:productOrServiceDialog.fields.remarks')}
              value={data.notes ?? ''}
              onChange={v => set('notes', v)}
            />
          </div>
        </div>
      </div>
    </ModalDialog>
  );
};

export default ProductOrServiceDialog;

